import {
  getPeerReviewModelForWorkflow,
  IPeerReviewModelConfig,
  IJournalPeerReviewModel,
  ISubmissionContext,
  journalPeerReviewModels
} from '@publishing-engine/domain/models';
import {
  buildRadioOptions,
  GeneralInfoEditableFieldConfig,
  GeneralInfoRadioOption
} from './generalInfoConfig';

const PEER_REVIEW_MODEL_SUBTEXTS: Record<IJournalPeerReviewModel, string> = {
  doubleAnonymous: 'Author identity may be concealed from the reviewers.',
  singleAnonymous: 'Author identity will be known to the reviewers.',
  openPeerReview:
    'Authors and reviewers know each other’s identity throughout the process.'
};

/**
 * The models an admin can choose from, shared by the row's edit modal and the
 * add-article-type step so both offer the same list.
 */
export const PEER_REVIEW_MODEL_OPTIONS: GeneralInfoRadioOption[] =
  buildRadioOptions(journalPeerReviewModels, PEER_REVIEW_MODEL_SUBTEXTS);

/**
 * The legacy value applied to the whole journal, so spreading it over the
 * journal's article types keeps every other row on the model it had before one
 * of them is written as a per-workflow entry. A workflow with no article type
 * on this journal cannot be listed here and falls back to the default model.
 */
const expandLegacyPeerReviewModel = (
  journal: ISubmissionContext,
  model: IJournalPeerReviewModel
): IPeerReviewModelConfig[] =>
  (journal.articleTypes ?? []).map(articleType => ({
    articleWorkflowId: articleType.articleWorkflowId,
    model
  }));

/**
 * `config.peerReviewModel` is being migrated from one per-journal string to a
 * per-article-workflow list (see migrate-peer-review-model), so either shape can
 * still come back from the API. The legacy string applied to the whole journal,
 * so it is read back as the entry of every article workflow.
 */
const readPeerReviewModelValue = (journal: ISubmissionContext) => {
  const configured = journal.config?.peerReviewModel as unknown;

  if (typeof configured === 'string') {
    return {
      legacyModel: configured as IJournalPeerReviewModel,
      entries: [] as IPeerReviewModelConfig[]
    };
  }

  if (!Array.isArray(configured)) {
    return { legacyModel: undefined, entries: [] as IPeerReviewModelConfig[] };
  }

  const entries = configured.filter(
    (entry): entry is IPeerReviewModelConfig =>
      !!entry && typeof entry === 'object' && 'articleWorkflowId' in entry
  );

  if (entries.length) return { legacyModel: undefined, entries };

  // Mongoose hydrates the legacy string through the array type as [model].
  return {
    legacyModel: configured.find(
      (entry): entry is IJournalPeerReviewModel => typeof entry === 'string'
    ),
    entries: [] as IPeerReviewModelConfig[]
  };
};

/**
 * The list as it should be written back: the migrated list when there is one,
 * otherwise the legacy per-journal model spread over the article types the
 * journal has, so storing one row's model keeps every other row where it was.
 */
const readConfiguredPeerReviewModels = (
  journal: ISubmissionContext
): IPeerReviewModelConfig[] => {
  const { legacyModel, entries } = readPeerReviewModelValue(journal);

  return legacyModel
    ? expandLegacyPeerReviewModel(journal, legacyModel)
    : entries;
};

/**
 * The model an article workflow has been given explicitly, or `undefined` when
 * nothing has been chosen for it. The distinction matters where "not configured
 * yet" has to be told apart from the model the journal would fall back to: the
 * Article Types table shows it, and activating a journal requires every article
 * type to have one.
 */
export const getConfiguredPeerReviewModel = (
  journal: ISubmissionContext,
  articleWorkflowId: string | undefined
): IJournalPeerReviewModel | undefined => {
  const { legacyModel, entries } = readPeerReviewModelValue(journal);

  if (legacyModel) return legacyModel;

  if (!articleWorkflowId) return undefined;

  return entries.find(entry => entry.articleWorkflowId === articleWorkflowId)
    ?.model;
};

/** The subset of `articleWorkflowIds` with no peer review model configured. */
export const getArticleWorkflowIdsMissingPeerReviewModel = (
  journal: ISubmissionContext,
  articleWorkflowIds: string[]
): string[] => {
  const missing = articleWorkflowIds.filter(
    articleWorkflowId =>
      !getConfiguredPeerReviewModel(journal, articleWorkflowId)
  );

  return missing;
};

/**
 * The Peer Review Model of a single article type, edited with the same modal the
 * General tab's fields use. The model is configured per article workflow, so the
 * field config is built for one workflow at a time rather than shared like
 * `GENERAL_INFO_EDITABLE_FIELD_MAP`.
 */
export const buildPeerReviewModelFieldConfig = (
  articleWorkflowId: string
): GeneralInfoEditableFieldConfig => ({
  key: 'peerReviewModel',
  label: 'Peer Review Model',
  modalTitle: 'Edit Peer Review Model',
  inputType: 'radio',
  isRequired: true,
  subText:
    'Changing the model updates which review fields authors are asked for on this article type.',
  getValue: journal =>
    getPeerReviewModelForWorkflow(journal.config, articleWorkflowId),
  /**
   * The endpoint stores the list as it receives it, so the whole list is sent
   * back with only this article type's entry replaced — sending the single entry
   * would clear the model of every other article type.
   */
  buildPayload: (value, journal) => {
    const configured = readConfiguredPeerReviewModels(journal);
    const model = value as IJournalPeerReviewModel;
    const hasEntry = configured.some(
      entry => entry.articleWorkflowId === articleWorkflowId
    );

    return {
      config: {
        peerReviewModel: hasEntry
          ? configured.map(entry =>
              entry.articleWorkflowId === articleWorkflowId
                ? { ...entry, model }
                : entry
            )
          : [...configured, { articleWorkflowId, model }]
      }
    };
  },
  options: PEER_REVIEW_MODEL_OPTIONS
});
